import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { CONTENT_PROVIDER_IDS } from 'app/constants/BibConstants';
import { getUpcFromCatalogBib } from 'app/helpers/bib/bibHelpers';
import { selectBibEntities, selectCatalogBibEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import syndeticsUnbound from 'app/utils/syndenticsUnbound';
import MoreFromProviderContainer from './MoreFromProviderContainer';
import MoreFromSyndeticsUnbound from './MoreFromSyndeticsUnbound';

const MoreFromSyndeticsUnboundContainer = ({ className, metadataId, showHeading }) => {
  const unboundContentRef = useRef(null);
  const bibs = useSelector(selectBibEntities);
  const catalogBibs = useSelector(selectCatalogBibEntities);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const [enrichmentIds, setEnrichmentIds] = useState([]);
  const [hasContent, setHasContent] = useState(false);

  const isEnabled = currentLibrary.getIn(['syndeticsUnbound', 'isEnabled']) ?? false;
  const accountId = currentLibrary.getIn(['syndeticsUnbound', 'accountId']) ?? null;
  const instanceId = currentLibrary.getIn(['syndeticsUnbound', 'instanceId']) ?? null;
  const shouldInitialize = isEnabled && Boolean(accountId);

  const isbn = bibs.getIn([metadataId, 'briefInfo', 'isbns', 0]);
  const upc = getUpcFromCatalogBib(catalogBibs.get(metadataId));

  useEffect(() => {
    if (!shouldInitialize) {
      return;
    }

    syndeticsUnbound
      .initialize(accountId, instanceId)
      .then(connectorApi => {
        const { getAvailableEnrichmentIds, populate } = connectorApi;

        const availableEnrichmentIds = getAvailableEnrichmentIds({
          excludeIds: [
            syndeticsUnbound.SUPPORTED_ENRICHMENT_IDS.READING_LEVEL,
            syndeticsUnbound.SUPPORTED_ENRICHMENT_IDS.SERIES
          ]
        });

        setEnrichmentIds(availableEnrichmentIds);

        populate({
          isbn,
          upc
        });
      })
      .catch(() => null);
  }, [accountId, instanceId, isbn, shouldInitialize, upc]);

  useEffect(() => {
    const DELAY_MULTIPLIER_SECONDS = 3;
    const MAX_CALL_COUNT = 3;
    let callCount = 0;
    let timeout;

    const recursiveTimeoutCallback = () => {
      const hasProviderContent = Boolean(unboundContentRef.current?.textContent);

      if (hasProviderContent) {
        setHasContent(true);
      } else if (callCount <= MAX_CALL_COUNT) {
        timeout = setTimeout(recursiveTimeoutCallback, DELAY_MULTIPLIER_SECONDS ** callCount * 1000);
      }

      callCount += 1;
    };

    recursiveTimeoutCallback();

    return () => {
      clearTimeout(timeout);
    };
  }, [setHasContent]);

  const renderOverlayContent = () => {
    /* eslint-disable-next-line react/no-danger */
    return <div dangerouslySetInnerHTML={{ __html: unboundContentRef.current?.innerHTML }}></div>;
  };

  return (
    <div className={className} style={{ display: hasContent ? 'block' : 'none' }}>
      <div ref={unboundContentRef} style={{ display: 'none' }}>
        <MoreFromSyndeticsUnbound enrichmentIds={enrichmentIds} />
      </div>
      <MoreFromProviderContainer
        metadataId={metadataId}
        providerId={CONTENT_PROVIDER_IDS.SYNDETICS_UNBOUND}
        renderOverlayContent={renderOverlayContent}
        showHeading={showHeading}
      />
    </div>
  );
};

MoreFromSyndeticsUnboundContainer.defaultProps = {
  className: '',
  showHeading: true
};

MoreFromSyndeticsUnboundContainer.propTypes = {
  className: PropTypes.string,
  metadataId: PropTypes.string.isRequired,
  showHeading: PropTypes.bool
};

export default MoreFromSyndeticsUnboundContainer;
